import { matchPath, useLocation } from 'react-router-dom';

import { Breadcrumbs, type ExtraCrumb } from '@/components/layout/Breadcrumb';
import { RouterConfig, CanvasRoutes } from '@/configs/routes';
import { decodeParams } from '@/utils/text';

interface PageProps {
  children?: React.ReactNode;
  // Usually the page name (string); may be a node when part of the heading is interactive (e.g. a linked id).
  title?: React.ReactNode;
  subheader?: React.ReactNode;
  actions?: React.ReactNode;
  // Trailing breadcrumb segments appended after the route trail.
  extraCrumbs?: ExtraCrumb[];
  // When true, the page fills the available height and the body is height-bounded (header band stays fixed)
  // so a full-height list/detail child can manage its own internal scrolling instead of the whole page scrolling.
  fillHeight?: boolean;
}

function usePageTitle(): string {
  const { pathname } = useLocation();

  const entries = Object.entries(RouterConfig) as Array<[CanvasRoutes, (typeof RouterConfig)[CanvasRoutes]]>;

  const match = entries
    .flatMap(([, cfg]) => {
      const m = matchPath({ path: cfg.path, end: false }, pathname);
      if (!m) return [];
      const bc = (cfg as { breadcrumb?: string | ((p: Record<string, string>) => string) }).breadcrumb;
      const label =
        typeof bc === 'function'
          ? bc(decodeParams((m.params ?? {}) as Record<string, string>))
          : typeof bc === 'string'
            ? bc
            : cfg.name;
      return [{ depth: m.pathnameBase.length, label }];
    })
    .sort((a, b) => b.depth - a.depth)[0];

  return match ? match.label : '';
}

export function Page({ children, title, subheader, actions, extraCrumbs, fillHeight }: PageProps) {
  const derivedTitle = usePageTitle();
  const heading = title ?? derivedTitle;

  return (
    <div className={`flex flex-col flex-1 min-h-0 bg-[#F5F6F8] ${fillHeight ? 'overflow-hidden' : 'overflow-y-auto'}`}>
      <div className="bg-white border-b border-[#E4E0D8] px-6 pt-4 pb-4">
        <Breadcrumbs extra={extraCrumbs} />
        <div className="flex items-center justify-between gap-3">
          {heading && <h1 className="text-[20px] font-semibold text-[#1A1C2E]">{heading}</h1>}
          {actions}
        </div>
        {subheader && <div className="text-[11px] text-[#9295A5]">{subheader}</div>}
      </div>
      {/* fillHeight: body is height-bounded so a full-height child scrolls internally (header band stays fixed). */}
      <div className={`flex-1 px-6 py-6 ${fillHeight ? 'min-h-0' : ''}`}>{children}</div>
    </div>
  );
}
